if (!customElements.get('mh-classic-gallery')) {
  customElements.define('mh-classic-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.scope = this.closest('product-info');
      this.image = this.querySelector('.mh-gallery__image');
      this.heart = this.querySelector('.mh-gallery__heart');
      this.dialog = this.querySelector('dialog');
      const signal = this.controller.signal;
      this.moveDescriptionVideos();
      this.showColor(this.selectedColor() || this.dataset.selectedColor);
      if (!this.scope.querySelector('.mh-classic__description .rte')) {
        this.descriptionObserver = new MutationObserver(() => {
          if (this.scope.querySelector('.mh-classic__description .rte')) {
            this.moveDescriptionVideos();
            this.filterColorImages(this.selectedColor() || this.dataset.selectedColor);
            this.descriptionObserver.disconnect();
          }
        });
        this.descriptionObserver.observe(this.scope, { childList: true, subtree: true });
      }
      this.addEventListener('click', event => {
        const thumb = event.target.closest('.mh-gallery__thumb');
        if (thumb?.dataset.color) this.selectColor(thumb.dataset.color);
        else if (thumb) this.showThumb(thumb);
        if (event.target.closest('.mh-gallery__zoom') && this.image) {
          const large = this.querySelector('.mh-gallery__large-image');
          large.src = this.image.src;
          large.alt = this.image.alt;
          this.dialog.showModal();
        }
        if (event.target.closest('.mh-gallery__close') || event.target === this.dialog) this.dialog.close();
      }, { signal });
      this.scope.addEventListener('change', event => {
        if (event.target.closest('variant-selects')) {
          const color = this.selectedColor();
          if (color) this.showColor(color);
        }
      }, { signal });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.sectionId || !data.variant) return;
          const color = data.variant.options[Number(this.dataset.colorPosition) - 1];
          this.showColor(color);
          const imageId = data.variant.featured_image?.id;
          const thumb = [...this.querySelectorAll('[data-image-id]')].find(item => String(item.dataset.imageId) === String(imageId) || item.dataset.mediaId === String(data.variant.featured_media?.id));
          if (thumb && !thumb.hidden) this.showThumb(thumb);
          this.heart.dataset.variantId = data.variant.id;
        });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.unsubscribe?.();
      this.descriptionObserver?.disconnect();
      this.querySelectorAll('video').forEach(video => video.pause());
    }

    videoSource(element) {
      const raw = element.getAttribute('src') || element.getAttribute('data-src') ||
        element.querySelector('source')?.getAttribute('src');
      if (!raw) return null;
      try {
        const url = new URL(raw, location.origin);
        if (!['https:', 'http:'].includes(url.protocol)) return null;
        const host = url.hostname.toLowerCase().replace(/^www\./, '');
        if (['youtube.com', 'youtube-nocookie.com', 'm.youtube.com', 'youtu.be'].includes(host)) {
          const id = host === 'youtu.be' ? url.pathname.split('/')[1] :
            url.searchParams.get('v') || url.pathname.match(/\/(?:embed|shorts|v|live)\/([^/?]+)/)?.[1];
          if (!/^[\w-]{11}$/.test(id || '')) return null;
          const embed = new URL(`https://www.youtube-nocookie.com/embed/${id}`);
          ['start', 'end', 'cc_load_policy', 'cc_lang_pref'].forEach(key => {
            if (url.searchParams.has(key)) embed.searchParams.set(key, url.searchParams.get(key));
          });
          embed.searchParams.set('enablejsapi', '1');
          embed.searchParams.set('origin', location.origin);
          embed.searchParams.set('playsinline', '1');
          embed.searchParams.set('rel', '0');
          return { key: `youtube:${id}`, type: 'iframe', url: embed.href };
        }
        if (['vimeo.com', 'player.vimeo.com'].includes(host)) {
          const id = url.pathname.match(/(?:^|\/)(\d+)(?:\/|$)/)?.[1];
          if (!id) return null;
          const embed = new URL(`https://player.vimeo.com/video/${id}`);
          if (url.searchParams.has('h')) embed.searchParams.set('h', url.searchParams.get('h'));
          return { key: `vimeo:${id}`, type: 'iframe', url: embed.href };
        }
        if (element.tagName === 'VIDEO' || /\.(mp4|webm|mov|m4v|ogv)$/i.test(url.pathname)) {
          return { key: `${url.origin}${decodeURI(url.pathname)}`, type: 'video', url: url.href };
        }
      } catch (_) { /* Leave unrecognized embeds in their original description. */ }
      return null;
    }

    moveDescriptionVideos() {
      const description = this.scope.querySelector('.mh-classic__description .rte');
      const rail = this.querySelector('.mh-gallery__rail');
      const stage = this.querySelector('.mh-gallery__stage');
      if (!description || !rail || !stage) return;
      const known = new Set();
      this.querySelectorAll('[data-media-panel]').forEach(panel => {
        const root = panel.querySelector('template')?.content || panel;
        root.querySelectorAll('video, iframe, source').forEach(element => {
          const source = this.videoSource(element);
          if (source) known.add(source.key);
        });
      });
      let anchor = rail.querySelector('.mh-gallery__thumb:not([hidden])') || rail.firstElementChild;
      let number = this.querySelectorAll('[data-description-video]').length;
      description.querySelectorAll('iframe, video, embed, object').forEach(element => {
        const source = this.videoSource(element);
        if (!source) return;
        if (!known.has(source.key)) {
          const label = `${this.dataset.videoLabel || 'Video'} ${++number}`;
          const id = `description-video-${this.dataset.sectionId}-${number}`;
          const panel = document.createElement('div');
          panel.className = 'mh-gallery__media';
          panel.dataset.mediaPanel = id;
          panel.dataset.descriptionVideo = 'true';
          panel.hidden = true;
          const template = document.createElement('template');
          const player = source.type === 'video' && element.tagName === 'VIDEO' ?
            element.cloneNode(true) : document.createElement(source.type);
          if (source.type === 'video') {
            if (element.tagName !== 'VIDEO') player.src = source.url;
            player.controls = true;
            player.playsInline = true;
            player.preload = 'none';
            player.autoplay = false;
            player.loop = false;
            player.removeAttribute('autoplay');
            player.removeAttribute('loop');
            player.removeAttribute('style');
            player.setAttribute('aria-label', label);
          } else {
            player.src = source.url;
            player.title = label;
            player.allow = 'encrypted-media; fullscreen; picture-in-picture';
            player.allowFullscreen = true;
            player.referrerPolicy = 'strict-origin-when-cross-origin';
          }
          template.content.append(player);
          panel.append(template);
          stage.append(panel);
          const thumb = document.createElement('button');
          thumb.type = 'button';
          thumb.className = 'mh-gallery__thumb mh-gallery__thumb--video';
          thumb.dataset.mediaId = id;
          thumb.setAttribute('aria-label', label);
          thumb.setAttribute('aria-pressed', 'false');
          thumb.innerHTML = '<span class="mh-gallery__video-art" aria-hidden="true"><svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="1.5"/><path d="m20 15 14 9-14 9z" fill="currentColor"/></svg></span>';
          const caption = document.createElement('span');
          caption.textContent = label;
          thumb.append(caption);
          if (anchor) anchor.after(thumb);
          else rail.append(thumb);
          anchor = thumb;
          known.add(source.key);
          this.classList.remove('mh-gallery--single');
        }
        // Remove only the moved player and any empty wrappers, preserving nearby copy.
        let parent = element.parentElement;
        if (element.tagName === 'VIDEO') element.pause();
        element.remove();
        while (parent && parent !== description && ['P', 'DIV', 'SPAN'].includes(parent.tagName) &&
          !parent.textContent.trim() && !parent.querySelector('img, picture, iframe, video, audio, object, embed, svg, table, ul, ol, hr, button, input')) {
          const next = parent.parentElement;
          parent.remove();
          parent = next;
        }
      });
    }

    selectedColor() {
      const fields = this.scope.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown');
      const field = fields[Number(this.dataset.colorPosition) - 1];
      return field?.querySelector('input:checked')?.value || field?.querySelector('select')?.value;
    }

    selectColor(color) {
      const fields = this.scope.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown');
      const field = fields[Number(this.dataset.colorPosition) - 1];
      if (!field) return;
      const radio = [...field.querySelectorAll('input[type="radio"]')].find(input => input.value === color);
      if (radio) {
        if (!radio.checked) radio.click();
        else this.showColor(color);
        return;
      }
      const select = field.querySelector('select');
      if (select && [...select.options].some(option => option.value === color)) {
        select.value = color;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    showColor(color) {
      if (!Number(this.dataset.colorPosition)) return;
      const thumbs = this.filterColorImages(color);
      const active = thumbs.find(thumb => thumb.dataset.color === color) || thumbs[0];
      this.showThumb(active);
    }

    filterColorImages(color) {
      const hasColor = Number(this.dataset.colorPosition) > 0;
      this.querySelectorAll('.mh-gallery__thumb').forEach(thumb => {
        let colors = [];
        try { colors = JSON.parse(thumb.dataset.imageColors || '[]'); } catch (_) { /* Unmapped image. */ }
        if (thumb.dataset.color) colors.push(thumb.dataset.color);
        thumb.hidden = Boolean(hasColor && thumb.dataset.image &&
          Number(this.dataset.colorCount) > 1 && !colors.includes(color));
      });
      const visible = [...this.querySelectorAll('.mh-gallery__thumb:not([hidden])')];
      const firstVideo = visible.find(thumb => !thumb.dataset.image);
      if (firstVideo) visible.filter(thumb => thumb.dataset.image).forEach(thumb => firstVideo.before(thumb));
      this.classList.toggle('mh-gallery--single', visible.length === 0 ||
        (visible.length === 1 && Boolean(visible[0].dataset.image)));
      return visible.filter(thumb => thumb.dataset.image);
    }

    showThumb(active) {
      this.querySelectorAll('.mh-gallery__thumb').forEach(thumb => thumb.setAttribute('aria-pressed', String(thumb === active)));
      const zoom = this.querySelector('.mh-gallery__zoom');
      if (zoom) zoom.hidden = !active?.dataset.image;
      const placeholder = this.querySelector('.mh-gallery__placeholder');
      if (placeholder) placeholder.hidden = true;
      const empty = this.querySelector('.mh-gallery__empty');
      if (empty) empty.hidden = Boolean(active);
      this.querySelectorAll('[data-media-panel]').forEach(panel => {
        const selected = panel.dataset.mediaPanel === active?.dataset.mediaId && !active?.dataset.image;
        panel.hidden = !selected;
        if (selected && panel.querySelector('template') && panel.dataset.loaded !== 'true') {
          const template = panel.querySelector('template');
          panel.append(template.content.cloneNode(true));
          panel.dataset.loaded = 'true';
          if (panel.dataset.descriptionVideo !== 'true') template.remove();
          const video = panel.querySelector('video');
          if (video) video.preload = 'metadata';
        }
        if (!selected) {
          panel.querySelector('video')?.pause();
          panel.querySelector('model-viewer')?.pause?.();
          const iframe = panel.querySelector('iframe');
          if (iframe) {
            if (panel.dataset.descriptionVideo === 'true') {
              iframe.remove();
              delete panel.dataset.loaded;
            } else {
              iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
              iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
            }
          }
        }
      });
      if (!active) return;
      const src = active.dataset.image;
      if (src && this.image && this.image.getAttribute('src') !== src) {
        const widths = [400, 600, 800, 1000, 1200];
        this.image.srcset = widths.map(width => {
          const url = new URL(src, location.origin);
          url.searchParams.set('width', width);
          return `${url.href} ${width}w`;
        }).join(', ');
        this.image.src = src;
        this.image.width = Number(active.dataset.imageWidth);
        this.image.height = Number(active.dataset.imageHeight);
        this.image.alt = active.querySelector('img')?.alt || active.dataset.color || '';
      }
      const rail = this.querySelector('.mh-gallery__rail');
      const railRect = rail.getBoundingClientRect();
      const thumbRect = active.getBoundingClientRect();
      if (window.matchMedia('(min-width: 750px)').matches) {
        if (thumbRect.top < railRect.top || thumbRect.bottom > railRect.bottom) {
          rail.scrollTop += thumbRect.top - railRect.top;
        }
      } else if (thumbRect.left < railRect.left || thumbRect.right > railRect.right) {
        rail.scrollLeft += thumbRect.left - railRect.left;
      }
    }
  });
}
